import type { Todo } from "../../domain/entities.ts";

export interface LocatedTaskReference { id: string; rootId: string; title: string }

export interface TaskOpenCapabilities {
  crossSpace?(): boolean;
  tasks(): Todo[];
  selectedId(): string | null;
  editing(): boolean;
  filter(): string;
  recentTasks(): LocatedTaskReference[];
  locate?(id: string): LocatedTaskReference | undefined | Promise<LocatedTaskReference | undefined>;
  confirmDiscard(): Promise<boolean>;
  resetMoreDisclosure(): void;
  leavePresence(id: string): void;
  setLastTrigger(id: string): void;
  activateRoot(rootId: string, clearCollection: boolean): Promise<void>;
  rememberRecent(task: Todo): Promise<void>;
  clearRecoveredDraft(): void;
  loadComments(id: string): Promise<void>;
  selectViewTask(id: string): void;
  render(animate?: boolean): Promise<void>;
  measure(startedAt: number): void;
  afterEntry(id: string): Promise<void>;
  startPresence(id: string): void;
  loadActivity(id: string): void;
  loadAttachments(id: string): void;
  removeFavorite(id: string): Promise<void>;
  toast(message: string, error?: boolean): void;
  now(): number;
}

export interface TaskOpenController {
  open(id: string): Promise<void>;
  openLocated(id: string): Promise<void>;
}

export function createTaskOpenController(capabilities: TaskOpenCapabilities): TaskOpenController {
  const open = async (id: string, discardConfirmed = false) => {
    const startedAt = capabilities.now();
    if (!discardConfirmed && !await capabilities.confirmDiscard()) return;
    const selected = capabilities.selectedId();
    if (selected !== id) capabilities.resetMoreDisclosure();
    if (selected && selected !== id) capabilities.leavePresence(selected);
    capabilities.setLastTrigger(id);
    const task = capabilities.tasks().find(item => item.id === id);
    if (task && (capabilities.filter() === "myday" || capabilities.crossSpace?.())) await capabilities.activateRoot(task.rootId, false);
    if (task) await capabilities.rememberRecent(task);
    capabilities.clearRecoveredDraft();
    await capabilities.loadComments(id);
    capabilities.selectViewTask(id);
    await capabilities.render();
    capabilities.measure(startedAt);
    void capabilities.afterEntry(id).then(() => {
      if (capabilities.selectedId() !== id || capabilities.editing()) return;
      capabilities.startPresence(id);
      capabilities.loadActivity(id);
      capabilities.loadAttachments(id);
    });
  };
  return {
    open,
    openLocated: async id => {
      const reference = capabilities.locate ? await capabilities.locate(id) : capabilities.recentTasks().find(item => item.id === id);
      if (!reference) { await capabilities.removeFavorite(id); capabilities.toast("That task is no longer available", true); return; }
      if (!await capabilities.confirmDiscard()) return;
      await capabilities.activateRoot(reference.rootId, true);
      await open(id, true);
    },
  };
}
